import React from "react";
import { api, upload } from "../editorial/api";
import { Project, charCount } from "../editorial/types";
import ProjectPicker, { projectLabel } from "../editorial/ProjectPicker";
import MarkdownBody from "../editorial/MarkdownBody";
import Byline from "./Byline";
import { AdminArticle, ArticleFields, CATEGORIES, LIMITS, categoryLabel } from "./types";

// Live character count under a field. The server counts code points
// (Python len()), so does charCount; the limit is shown before the editor
// reaches it and an over-limit value is kept, flagged, and refused on save.
export function Counter({ value, limit, label }: { value: string; limit: number; label: string }) {
  const n = charCount(value),
    over = n > limit;
  return (
    <span className={"er-counter" + (over ? " er-over" : "")} aria-live="polite">
      {n} / {limit} characters
      {over && <> · Please shorten the {label} to {limit} characters or fewer.</>}
    </span>
  );
}

function overLimit(f: ArticleFields) {
  return (Object.keys(LIMITS) as (keyof typeof LIMITS)[]).some((k) => charCount(f[k] || "") > LIMITS[k]);
}

// Centre-square crop to 512 px for the author photo, done in the browser
// before upload. Returns the original file when the canvas is unavailable.
async function cropSquare(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const side = Math.min(bitmap.width, bitmap.height),
      out = Math.min(512, side),
      canvas = document.createElement("canvas");
    canvas.width = canvas.height = out;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, out, out);
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    const blob: Blob | null = await new Promise((r) => canvas.toBlob(r, type, 0.9));
    return blob ? new File([blob], "photo." + (type === "image/png" ? "png" : "jpg"), { type }) : file;
  } catch {
    return file;
  }
}

function statusOf(a: AdminArticle) {
  if (!a.published) return a.canonicalSlug ? "unpublished" : "draft";
  return a.publishedRevision === a.revision ? "published" : "published · draft changes";
}

export default function BlogDesk({ projects, username }: { projects: Project[]; username: string }) {
  const [articles, setArticles] = React.useState<AdminArticle[] | null>(null),
    [selected, setSelected] = React.useState<AdminArticle | null>(null),
    [fields, setFields] = React.useState<ArticleFields | null>(null),
    [linked, setLinked] = React.useState(false),
    [error, setError] = React.useState(""),
    [notice, setNotice] = React.useState(""),
    [busy, setBusy] = React.useState(false),
    [newCategory, setNewCategory] = React.useState("perspectives"),
    [filterProject, setFilterProject] = React.useState(""),
    [audit, setAudit] = React.useState<{ action: string; actorId: string; createdAt: string }[] | null>(null);
  const bodyRef = React.useRef<HTMLTextAreaElement>(null);

  async function load() {
    const data = await api<{ articles: AdminArticle[] }>("/admin/articles");
    setArticles(data.articles);
  }
  React.useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);

  const dirty = Boolean(selected && fields && JSON.stringify(fields) !== JSON.stringify(selected.draft));

  function accept(a: AdminArticle) {
    setSelected(a);
    setFields(a.draft);
    setLinked(Boolean(a.draft.projectId));
    setArticles((list) => [a, ...(list || []).filter((x) => x.id !== a.id)]);
  }
  function choose(a: AdminArticle) {
    if (busy) return;
    if (dirty && !window.confirm("You have unsaved changes. Switch anyway?")) return;
    setSelected(a);
    setFields(a.draft);
    setLinked(Boolean(a.draft.projectId));
    setAudit(null);
    setError("");
    setNotice("");
  }
  async function operation(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function edit(name: keyof ArticleFields, value: string) {
    if (busy) return;
    setFields((f) => (f ? { ...f, [name]: value } : f));
  }
  async function save() {
    if (!selected || !fields) return;
    if (overLimit(fields)) throw new Error("A field is over its character limit. Shorten it and try again.");
    const saved = await api<AdminArticle>("/admin/articles/" + selected.id, "PUT", {
      revision: selected.revision,
      fields: { ...fields, projectId: linked ? fields.projectId : "" },
    });
    accept(saved);
    setNotice("Draft saved. Readers still see the published version, if any.");
    return saved;
  }
  async function publish() {
    if (!selected || !fields) return;
    const saved = dirty ? await save() : selected;
    if (!saved) return;
    const p = await api<AdminArticle>("/admin/articles/" + saved.id + "/publish", "POST", { revision: saved.revision });
    accept(p);
    setNotice("Article published.");
  }
  async function previewPage() {
    if (!selected) return;
    const saved = dirty ? await save() : selected;
    if (saved) window.open("/blog/preview/" + saved.id, "_blank", "noopener");
  }
  async function pickImage(file: File | undefined, target: "cover" | "photo" | "body") {
    if (!file) return;
    await operation(async () => {
      if (file.size > 5 * 1024 * 1024) throw new Error("Images must be 5 MB or smaller.");
      const image = await upload(target === "photo" ? await cropSquare(file) : file);
      if (target === "cover") {
        edit("coverUrl", image.url);
        setNotice("Cover uploaded. Add its alt text (and a caption or credit) before publishing.");
      } else if (target === "photo") {
        edit("authorPhotoUrl", image.url);
        setNotice("Author photo uploaded (cropped to a square). Add a photo description before publishing.");
      } else {
        const ta = bodyRef.current,
          snippet = "\n\n![Describe the image](" + image.url + ' "Caption or credit")\n\n';
        const body = fields?.bodyMarkdown || "";
        const at = ta ? ta.selectionStart : body.length;
        edit("bodyMarkdown", body.slice(0, at) + snippet + body.slice(at));
        setNotice("Image inserted into the body. Replace the alt text and caption in the brackets.");
      }
    });
  }

  const shownArticles = (articles || []).filter(
    (a) => !filterProject || (a.published || a.draft).projectId === filterProject || a.draft.projectId === filterProject,
  );
  const previewAuthor = fields
    ? {
        name: fields.authorName || "Author name",
        role: fields.authorRole,
        bio: fields.authorBio,
        photoUrl: fields.authorPhotoUrl,
        photoAlt: fields.authorPhotoAlt,
        initials: fields.authorName
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((p) => p[0])
          .join("")
          .toUpperCase(),
      }
    : null;
  const linkedProject = projects.find((p) => p.id === fields?.projectId);

  return (
    <div className="bl-desk">
      <div className="er-admin-picker bl-desk-top">
        <label>
          Edit an existing article
          <select
            aria-label="Edit an existing article"
            value={selected?.id || ""}
            disabled={busy || !articles}
            onChange={(e) => {
              const a = (articles || []).find((a) => a.id === e.target.value);
              if (a) choose(a);
            }}
          >
            <option value="">{articles ? "Choose an article…" : "Loading…"}</option>
            {shownArticles.map((a) => {
              const f = a.published || a.draft,
                p = projects.find((p) => p.id === f.projectId);
              return (
                <option key={a.id} value={a.id}>
                  {(f.title || "Untitled") + " · " + categoryLabel(f.category) + " · " + (f.authorName || "no author") + " · " + statusOf(a) + (p ? " · " + p.year + " " + p.team : "")}
                </option>
              );
            })}
          </select>
        </label>
        <label>
          Filter by project
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
            <option value="">All articles</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.year} · {p.team}
              </option>
            ))}
          </select>
        </label>
        <div className="er-new-blog">
          <label>
            New article
            <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <button
            disabled={busy}
            onClick={() =>
              operation(async () => {
                if (dirty) throw new Error("Save your changes first.");
                const a = await api<AdminArticle>("/admin/articles", "POST", { category: newCategory });
                accept(a);
                setAudit(null);
                setNotice("New article created as a draft. Nothing is public until you publish it.");
              })
            }
          >
            New article
          </button>
        </div>
      </div>
      {error && (
        <p role="alert" className="er-error">
          {error}
        </p>
      )}
      <p className="er-notice" aria-live="polite">
        {notice}
      </p>
      {selected && fields && previewAuthor ? (
        <>
          <div className="er-state">
            <span>
              {selected.published ? "Published version live" : "Not published yet"}
              {dirty ? " · unsaved changes" : selected.published && selected.publishedRevision !== selected.revision ? " · draft changes not yet published" : ""}
            </span>
            <span>{categoryLabel(fields.category)}{linkedProject ? " · " + linkedProject.year + " · " + linkedProject.team : " · no project"}</span>
          </div>
          <a className="er-jump-preview" href="#bl-draft-preview">
            Jump to the draft preview ↓
          </a>
          <div className="er-admin-layout">
            <div className="er-edit-form">
              <label>
                Category
                <select value={fields.category} disabled={busy} onChange={(e) => edit("category", e.target.value)}>
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Title · {LIMITS.title} characters maximum
                <input value={fields.title} onChange={(e) => edit("title", e.target.value)} />
                <Counter value={fields.title} limit={LIMITS.title} label="title" />
              </label>
              <label>
                URL slug (fixed after first publication)
                <input
                  placeholder="beyond-the-score"
                  value={fields.slug}
                  disabled={Boolean(selected.canonicalSlug)}
                  onChange={(e) => edit("slug", e.target.value.toLowerCase())}
                />
              </label>
              <label>
                Standfirst · {LIMITS.standfirst} characters maximum
                <textarea rows={3} value={fields.standfirst} onChange={(e) => edit("standfirst", e.target.value)} />
                <Counter value={fields.standfirst} limit={LIMITS.standfirst} label="standfirst" />
              </label>
              <fieldset className="bl-fieldset">
                <legend>Author</legend>
                <p className="er-help">The byline is the author’s, not the signed-in account ({username}). Personal or institutional names both work.</p>
                <label>
                  Author name
                  <input value={fields.authorName} onChange={(e) => edit("authorName", e.target.value)} />
                </label>
                <label>
                  Author role (optional)
                  <input placeholder="Judge, iDEC 2025" value={fields.authorRole} onChange={(e) => edit("authorRole", e.target.value)} />
                </label>
                <label>
                  Author bio (optional) · {LIMITS.authorBio} characters maximum
                  <textarea rows={3} value={fields.authorBio} onChange={(e) => edit("authorBio", e.target.value)} />
                  <Counter value={fields.authorBio} limit={LIMITS.authorBio} label="bio" />
                </label>
                <label>
                  Author photo (optional; cropped to a square, PNG / JPEG / WebP up to 5 MB)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy}
                    onChange={(e) => {
                      pickImage(e.target.files?.[0], "photo");
                      e.target.value = "";
                    }}
                  />
                </label>
                <div className="bl-photo-row">
                  <Byline author={previewAuthor} size={56} />
                  {fields.authorPhotoUrl && (
                    <button
                      onClick={() => {
                        edit("authorPhotoUrl", "");
                        edit("authorPhotoAlt", "");
                      }}
                    >
                      Remove photo
                    </button>
                  )}
                </div>
                {fields.authorPhotoUrl && (
                  <label>
                    Photo description
                    <input value={fields.authorPhotoAlt} onChange={(e) => edit("authorPhotoAlt", e.target.value)} />
                  </label>
                )}
                <p className="er-help">Without a photo the byline shows the author’s initials. Never upload a generated or borrowed likeness of a real person.</p>
              </fieldset>
              <fieldset className="bl-fieldset">
                <legend>Cover image</legend>
                <label>
                  Upload (PNG / JPEG / WebP, up to 5 MB)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy}
                    onChange={(e) => {
                      pickImage(e.target.files?.[0], "cover");
                      e.target.value = "";
                    }}
                  />
                </label>
                {fields.coverUrl && (
                  <div>
                    <img className="er-upload-preview" src={fields.coverUrl} alt={fields.coverAlt || "Cover preview"} />
                    <button
                      onClick={() => {
                        edit("coverUrl", "");
                        edit("coverAlt", "");
                        edit("coverCaption", "");
                      }}
                    >
                      Remove cover
                    </button>
                  </div>
                )}
                <label>
                  Alt text
                  <input value={fields.coverAlt} onChange={(e) => edit("coverAlt", e.target.value)} />
                </label>
                <label>
                  Caption / credit
                  <input value={fields.coverCaption} onChange={(e) => edit("coverCaption", e.target.value)} />
                </label>
                <p className="er-help">The cover, the author photo and the body images are separate: changing one never replaces another, and none of them comes from a project Insight.</p>
              </fieldset>
              <label>
                Body
                <textarea ref={bodyRef} rows={18} value={fields.bodyMarkdown} onChange={(e) => edit("bodyMarkdown", e.target.value)} />
                <Counter value={fields.bodyMarkdown} limit={LIMITS.bodyMarkdown} label="body" />
              </label>
              <div className="bl-body-tools">
                <label className="er-file-button">
                  Insert image at the cursor…
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy}
                    onChange={(e) => {
                      pickImage(e.target.files?.[0], "body");
                      e.target.value = "";
                    }}
                  />
                </label>
                <span className="er-help">
                  Blank lines separate paragraphs; # / ## / ### make headings; “- ” starts a list; [text](https://…) makes a link; ![alt](/media/… "caption") shows an image.
                </span>
              </div>
              <label className="er-check">
                <input
                  type="checkbox"
                  checked={linked}
                  disabled={busy}
                  onChange={(e) => {
                    setLinked(e.target.checked);
                    if (!e.target.checked) edit("projectId", "");
                  }}
                />
                Link to a project (optional)
              </label>
              {linked && (
                <ProjectPicker
                  projects={projects}
                  value={fields.projectId}
                  onChange={(id) => edit("projectId", id)}
                  allowNone
                  disabled={busy}
                />
              )}
              <p className="er-help">
                An article with no project is simply listed in its category. A linked article adds a “Related articles” entry to that project’s card in Inside the Research; the link can be changed after publication without changing the article’s URL.
              </p>
              <label>
                Source / rights note (editors only)
                <textarea rows={3} value={fields.rightsNote} onChange={(e) => edit("rightsNote", e.target.value)} />
                <Counter value={fields.rightsNote} limit={LIMITS.rightsNote} label="note" />
              </label>
              <div className="er-editor-actions">
                <button disabled={busy || !dirty} onClick={() => operation(save as () => Promise<void>)}>
                  Save draft
                </button>
                <button disabled={busy} onClick={() => operation(previewPage)} title="Opens the saved draft in the public article layout, in a new tab">
                  {dirty ? "Save and preview page ↗" : "Preview page ↗"}
                </button>
                <button
                  className="er-primary"
                  disabled={busy || (!dirty && Boolean(selected.published) && selected.publishedRevision === selected.revision)}
                  onClick={() => {
                    if (window.confirm(selected.published ? "Publish these changes to the live article?" : "Publish this article? It becomes public immediately."))
                      operation(publish);
                  }}
                >
                  {busy ? "Working…" : dirty ? "Save and publish" : selected.published ? "Publish changes" : "Publish"}
                </button>
                {selected.published && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Unpublish this article? Its page returns “not found” until it is published again.")) return;
                      operation(async () => {
                        const a = await api<AdminArticle>("/admin/articles/" + selected.id + "/unpublish", "POST", { revision: selected.revision });
                        accept(a);
                        setNotice("Article unpublished. Its URL is reserved and it no longer counts as a related article.");
                      });
                    }}
                  >
                    Unpublish
                  </button>
                )}
                {!selected.published && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Delete this draft? This cannot be undone.")) return;
                      operation(async () => {
                        await api("/admin/articles/" + selected.id, "DELETE");
                        setArticles((list) => (list || []).filter((x) => x.id !== selected.id));
                        setSelected(null);
                        setFields(null);
                        setNotice("Draft deleted.");
                      });
                    }}
                  >
                    Delete draft
                  </button>
                )}
              </div>
              <div className="er-editor-actions">
                <button
                  disabled={busy}
                  onClick={() =>
                    operation(async () => {
                      if (dirty) throw new Error("Save your changes before reloading.");
                      accept(await api<AdminArticle>("/admin/articles/" + selected.id));
                    })
                  }
                  title="Fetch the latest saved copy from the server"
                >
                  Reload from server
                </button>
                <button
                  onClick={() =>
                    operation(async () => {
                      const h = await api<{ items: { action: string; actorId: string; createdAt: string }[] }>("/admin/articles/" + selected.id + "/history");
                      setAudit(h.items);
                    })
                  }
                >
                  Show history
                </button>
                {selected.published && (
                  <a className="bl-view-live" href={"/blog/" + selected.published.slug} target="_blank" rel="noopener noreferrer">
                    View the public article ↗
                  </a>
                )}
              </div>
              {audit && (
                <div className="er-audit">
                  <h3>Recent changes</h3>
                  {audit.length ? (
                    audit.map((h, i) => (
                      <p key={i}>
                        {new Date(h.createdAt).toLocaleString()} · {h.action} · {h.actorId}
                      </p>
                    ))
                  ) : (
                    <p>No changes recorded yet.</p>
                  )}
                </div>
              )}
            </div>
            <aside className="er-draft-preview" id="bl-draft-preview">
              <div className="er-kicker">Editors only · draft preview</div>
              <article className="er-blog bl-article">
                <div className="er-kicker">{categoryLabel(fields.category)}</div>
                <h1>{fields.title || "Article title"}</h1>
                {fields.standfirst && <p className="er-standfirst">{fields.standfirst}</p>}
                <Byline author={previewAuthor} date={selected.publishedAt} />
                {fields.coverUrl && (
                  <figure className="bl-cover">
                    <img src={fields.coverUrl} alt={fields.coverAlt} />
                    {fields.coverCaption && <figcaption>{fields.coverCaption}</figcaption>}
                  </figure>
                )}
                <MarkdownBody text={fields.bodyMarkdown} />
                {linked && linkedProject && (
                  <p className="er-help">Related project: {projectLabel(linkedProject)}</p>
                )}
              </article>
              <p className="er-help">Saving a draft does not change the public site. This version goes live only when published.</p>
            </aside>
          </div>
        </>
      ) : (
        <p className="er-empty">Choose an article to edit, or create a new one. Articles need no project; a project link is optional and can be added in the form.</p>
      )}
    </div>
  );
}
